import React, { useState, useEffect } from "react";
import { useObserver } from "mobx-react";
import { useStores } from "../../store";
import { AiFillCloseCircle } from "react-icons/ai";
import { RiRefreshLine } from "react-icons/ri";
import Editor from "@monaco-editor/react";

export default function Compiler({ popUpInitialValue, handlePopup }) {
  const { InterviewStore, RecuiterStore } = useStores();
  const [code, setCode] = useState("// Write your code here");
  const [output, setOutput] = useState("");
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(71); // Default: Python
  const [stdin, setStdin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [theme, setTheme] = useState("vs-dark");
  const [fontSize, setFontSize] = useState(14);
  const [error, setError] = useState(null);

  const resetTranscriptByDom = () => {
    try {
      const buttonElement = document.getElementById("reset-transcript");
      if (buttonElement) {
        buttonElement?.click();
      }
      InterviewStore.setLastSubmitTimeStamp(); // this is for mobile view speech to text
    } catch (error) {}
  };

  useEffect(() => {
    // Fetch available languages
    handleReset();
    const fetchLanguages = async () => {
      try {
        const response = await RecuiterStore.fetchLanguages();
        console.log(response, "response");
        setLanguages(response);
      } catch (error) {
        setError("Failed to fetch languages. Please try again later.");
      }
    };
    fetchLanguages();
  }, []);

  const handleEditorChange = (value) => {
    setCode(value);
    InterviewStore.setUserVoiceInput(value);
    InterviewStore.resetTranscript();
    InterviewStore.setCompilerCode(value); // this is for mobile view speech to text
    resetTranscriptByDom();
    setError(null);
  };

  const onclickSubmit = (e) => {
    e.preventDefault();
    try {
      const buttonElement = document.getElementById("submit-button");
      if (buttonElement) {
        buttonElement.click();
        handlePopup();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRun = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await RecuiterStore.compileCode(
        code,
        selectedLanguage,
        stdin
      );
      setOutput(response.output || response.status);
    } catch (error) {
      setError(
        error.response?.data?.error ||
          "Failed to execute code. Please try again."
      );
      setOutput("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCode("// Write your code here");
    setStdin("");
    setOutput("");
    InterviewStore.setCompilerCode("");
    InterviewStore.setUserVoiceInput("");
    InterviewStore.resetTranscript();
    resetTranscriptByDom();
    setError(null);
  };

  const editorOptions = {
    fontSize,
    minimap: { enabled: false },
    wordWrap: "on",
    automaticLayout: true,
    scrollBeyondLastLine: false,
    lineNumbers: "on",
    renderLineHighlight: "all",
    matchBrackets: "always",
    autoClosingBrackets: "always",
    autoClosingQuotes: "always",
    formatOnPaste: true,
    formatOnType: true,
    tabSize: 2,
    scrollbar: {
      vertical: "visible",
      horizontal: "visible",
      useShadows: false,
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
    },
  };

  return useObserver(() => (
    <>
      {popUpInitialValue ? (
        <div
          className="absolute w-screen h-screen z-50 inset-0 backdrop-filter backdrop-blur-sm bg-black bg-opacity-60 flex items-center justify-center"
          onClick={handlePopup}
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative rounded-2xl bg-[#1a2233] shadow-2xl flex flex-col items-center w-[95vw] md:w-[90vw] lg:w-[80vw] h-[92vh] p-0 border border-[#232b3e]"
          >
            <AiFillCloseCircle
              className="text-red-400 hover:text-red-600 absolute text-3xl cursor-pointer top-4 right-4 transition-colors duration-150 z-10 focus:outline-none focus:ring-2 focus:ring-red-400"
              onClick={handlePopup}
              title="Close"
              tabIndex={0}
              aria-label="Close dialog"
            />
            <p className="text-white font-bold text-3xl mt-6 mb-2 tracking-wide select-none">
              Compiler
            </p>
            <div className="flex flex-col w-full h-full">
              {/* Header Controls */}
              <div className="flex flex-col md:flex-row w-full justify-between items-center px-8 py-3 gap-4 border-b border-[#232b3e] bg-[#202a40]">
                <div className="flex flex-row items-center gap-3 flex-wrap">
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(Number(e.target.value))}
                    className="rounded-lg px-3 py-2 bg-[#232b3e] text-white border border-[#2d3750] focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm transition-all duration-150"
                    title="Select Language"
                    aria-label="Select Language"
                  >
                    {languages.map((lang) => (
                      <option key={lang.id} value={lang.id}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 ml-2 bg-[#232b3e] rounded-lg px-2 py-1">
                    <button
                      onClick={() => setFontSize((f) => Math.max(8, f - 1))}
                      className="px-2 py-1 text-white rounded hover:bg-[#2d3750] focus:bg-[#2d3750] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      title="Decrease Font Size"
                      aria-label="Decrease Font Size"
                    >
                      -
                    </button>
                    <span className="text-white font-medium w-10 text-center select-none">
                      {fontSize}px
                    </span>
                    <button
                      onClick={() => setFontSize((f) => Math.min(24, f + 1))}
                      className="px-2 py-1 text-white rounded hover:bg-[#2d3750] focus:bg-[#2d3750] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      title="Increase Font Size"
                      aria-label="Increase Font Size"
                    >
                      +
                    </button>
                  </div>
                  <select
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    className="rounded-lg px-3 py-2 bg-[#232b3e] text-white border border-[#2d3750] focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm transition-all duration-150 ml-2"
                    title="Select Theme"
                    aria-label="Select Theme"
                  >
                    <option value="vs-dark">Dark</option>
                    <option value="light">Light</option>
                    <option value="hc-black">High Contrast</option>
                  </select>
                </div>
                <div className="flex flex-row gap-3 mt-2 md:mt-0">
                  <button
                    onClick={handleRun}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition-all duration-150 focus:ring-2 focus:ring-blue-400 focus:outline-none disabled:opacity-60"
                    disabled={isLoading}
                    title="Run Code"
                    aria-label="Run Code"
                  >
                    {isLoading ? "Running..." : "Run Code"}
                  </button>
                  <button
                    onClick={handleReset}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition-all duration-150 focus:ring-2 focus:ring-yellow-300 focus:outline-none"
                    title="Reset Editor"
                    aria-label="Reset Editor"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={onclickSubmit}
                    id="submit-button"
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition-all duration-150 focus:ring-2 focus:ring-green-400 focus:outline-none"
                    title="Submit Code"
                    aria-label="Submit Code"
                  >
                    Submit
                  </button>
                </div>
              </div>
              <div className="flex flex-row w-full h-full min-h-0">
                {/* Editor Section */}
                <div className="flex-1 w-full flex flex-col h-full overflow-hidden min-w-[300px]">
                  <div className="flex-1 w-full h-full flex flex-col bg-[#232b3e] p-3 min-w-[300px] rounded-bl-2xl border-r border-[#232b3e] shadow-inner relative">
                    <Editor
                      height="100%"
                      defaultLanguage="python"
                      language={languages.find((l) => l.id === selectedLanguage)?.editorLang || "python"}
                      theme={theme}
                      value={code}
                      onChange={handleEditorChange}
                      options={editorOptions}
                      className="h-full"
                    />
                  </div>
                </div>
                {/* Input/Output Section */}
                <div className="flex flex-col w-full md:w-[30%] h-full min-h-0">
                  {/* Input Section */}
                  <div className="bg-[#232b3e] p-3 flex-1 flex flex-col border-b border-[#232b3e] rounded-tr-2xl shadow-inner min-h-[120px]">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-white text-lg font-bold tracking-wide select-none">Input</h3>
                      <button
                        className="text-xs text-gray-300 hover:text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                        onClick={() => setStdin("")}
                        title="Clear Input"
                        aria-label="Clear Input"
                      >
                        Clear
                      </button>
                    </div>
                    <textarea
                      value={stdin}
                      onChange={(e) => setStdin(e.target.value)}
                      placeholder="Enter input here..."
                      className="w-full flex-1 min-h-[60px] bg-[#1a2233] text-white rounded-lg p-2 resize-none border border-[#2d3750] focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all duration-150 custom-scrollbar"
                      aria-label="Input Area"
                    />
                  </div>
                  {/* Output Section */}
                  <div className="bg-[#202a40] w-full px-4 py-4 flex-1 shadow-inner min-h-[120px] rounded-br-2xl">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-white text-lg font-bold tracking-wide select-none">Output</h3>
                      <button
                        className="text-xs text-gray-300 hover:text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                        onClick={() => {
                          setOutput("");
                          setError(null);
                        }}
                        title="Clear Output"
                        aria-label="Clear Output"
                      >
                        Clear
                      </button>
                    </div>
                    {error && (
                      <div className="text-red-400 text-xs mb-2 font-mono">{error}</div>
                    )}
                    <pre className="w-full flex-1 h-[90%] bg-[#1a2233] text-white rounded-lg p-2 overflow-x-auto border border-[#2d3750] font-mono text-sm transition-all duration-150 custom-scrollbar" aria-label="Output Area">
                      {output}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
            <style>{`
              .custom-scrollbar::-webkit-scrollbar {
                width: 8px;
                background: #232b3e;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: #2d3750;
                border-radius: 4px;
              }
              .custom-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: #2d3750 #232b3e;
              }
            `}</style>
          </div>
        </div>
      ) : null}
    </>
  ));
}
